/**
 * Notification Store (Zustand)
 *
 * Global state management for notification/inbox system.
 * Persists to localStorage for notification history across sessions.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type {
  Notification,
  NotificationType,
  NotificationPreferences,
} from '@shared/types/notifications';

/**
 * Notification store state and actions
 */
interface NotificationStore {
  // State
  notifications: Notification[];
  preferences: NotificationPreferences;

  // Actions
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => string;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  deleteNotification: (notificationId: string) => void;
  clearAllNotifications: () => void;
  setPreference: <K extends keyof NotificationPreferences>(
    key: K,
    value: NotificationPreferences[K]
  ) => void;
  setDoNotShow: (type: NotificationType, value: boolean) => void;
}

/**
 * Generate unique notification ID
 */
function generateNotificationId(): string {
  return `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Create notification store with localStorage persistence
 */
export const useNotificationStore = create<NotificationStore>()(
  persist(
    (set, get) => ({
      // Initial state
      notifications: [],
      preferences: {
        doNotShow: {},
        showBadge: true,
        autoDismissSuccess: true,
        autoDismissDuration: 3000,
      },

      /**
       * Add a new notification
       *
       * @param notification - Notification data (without id, timestamp, read)
       * @returns Generated notification ID
       */
      addNotification: (notification) => {
        const id = generateNotificationId();
        const timestamp = new Date().toISOString();

        // Check if user has "do not show" enabled for this type
        const { doNotShow } = get().preferences;
        if (doNotShow[notification.type as keyof typeof doNotShow]) {
          console.log(`[NotificationStore] Skipping notification type: ${notification.type} (do not show)`);
          return id; // Still return ID but don't add to store
        }

        const newNotification: Notification = {
          ...notification,
          id,
          timestamp,
          read: false,
        };

        set((state) => ({
          notifications: [newNotification, ...state.notifications],
        }));

        console.log(`[NotificationStore] Added notification:`, newNotification);
        return id;
      },

      /**
       * Mark notification as read
       *
       * @param notificationId - ID of notification to mark as read
       */
      markAsRead: (notificationId) => {
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.id === notificationId ? { ...n, read: true } : n
          ),
        }));
        console.log(`[NotificationStore] Marked as read: ${notificationId}`);
      },

      /**
       * Mark all notifications as read
       */
      markAllAsRead: () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
        }));
        console.log(`[NotificationStore] Marked all as read`);
      },

      /**
       * Delete a notification
       *
       * @param notificationId - ID of notification to delete
       */
      deleteNotification: (notificationId) => {
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== notificationId),
        }));
        console.log(`[NotificationStore] Deleted notification: ${notificationId}`);
      },

      /**
       * Clear all notifications
       */
      clearAllNotifications: () => {
        set({ notifications: [] });
        console.log(`[NotificationStore] Cleared all notifications`);
      },

      /**
       * Set a preference value
       *
       * @param key - Preference key
       * @param value - Preference value
       */
      setPreference: (key, value) => {
        set((state) => ({
          preferences: {
            ...state.preferences,
            [key]: value,
          },
        }));
        console.log(`[NotificationStore] Set preference: ${key} = ${JSON.stringify(value)}`);
      },

      /**
       * Set "do not show" flag for a notification type
       *
       * @param type - Notification type
       * @param value - Whether to hide this notification type
       */
      setDoNotShow: (type, value) => {
        set((state) => ({
          preferences: {
            ...state.preferences,
            doNotShow: {
              ...state.preferences.doNotShow,
              [type]: value,
            },
          },
        }));
        console.log(`[NotificationStore] Set do not show: ${type} = ${value}`);
      },
    }),
    {
      name: 'notification-storage', // localStorage key
      partialize: (state) => ({
        notifications: state.notifications,
        preferences: state.preferences,
      }),
    }
  )
);

/**
 * Helper hook to get unread count
 *
 * This is a selector that derives the count from notifications state.
 * Returns a primitive number, so no shallow comparison needed.
 */
export function useUnreadCount() {
  return useNotificationStore((state) => state.notifications.filter((n) => !n.read).length);
}

/**
 * Helper hook to get unread notifications
 *
 * Uses useShallow to prevent infinite re-renders when returning derived arrays.
 */
export function useUnreadNotifications() {
  return useNotificationStore(
    useShallow((state) => state.notifications.filter((n) => !n.read))
  );
}

/**
 * Helper hook to get recent notifications (last N)
 *
 * Uses useShallow to prevent infinite re-renders when returning derived arrays.
 */
export function useRecentNotifications(limit = 5) {
  return useNotificationStore(
    useShallow((state) => state.notifications.slice(0, limit))
  );
}

/**
 * Helper hook to get notification actions only
 *
 * Extracts only the action functions to prevent re-renders when notifications change.
 */
export function useNotificationActions() {
  return useNotificationStore(
    useShallow((state) => ({
      addNotification: state.addNotification,
      markAsRead: state.markAsRead,
      markAllAsRead: state.markAllAsRead,
      deleteNotification: state.deleteNotification,
      clearAllNotifications: state.clearAllNotifications,
      setPreference: state.setPreference,
      setDoNotShow: state.setDoNotShow,
    }))
  );
}
